<template>
	<div ref="guestUsageTable" class="guest-usage-table" :style="`--table-height: ${tableHeight}px;`">
		<div class="guest-usage-table__header bg-primary sticky flex justify-between items-center pb-3">
			<div class="flex items-center gap-3">
				<h2>Guests</h2>
				<CBadge class="mt-2" :text="usageList.length" />
			</div>
			<div class="flex items-center">
				<span class="mr-3">
					<CSubtleSelect
						:value="selectedDate"
						:options="date"
						append-to-body
						@change="updateDate"
					/>
				</span>
				<ElDropdown
					v-if="allGuestLists.length > 1"
					trigger="click"
					class="ml-auto"
					placement="bottom-start"
					@command="handleFilterChange"
				>
					<div class="el-dropdown-link">
						<CToolTip
							v-if="!currentFilter"
							placement="top"
							:use-button="false"
						>
							<template #button>
								<CIconButton
									icon="filter"
									aria-label="Filter Guests By Type"
									class="mr-2"
								/>
							</template>

							<span>Filter Guests</span>
						</CToolTip>

						<CBadge
							v-else
							:text="currentFilterName"
							icon="filter"
							class="text-icon-subtle mr-2 guest-usage-table__badge truncate"
						/>
					</div>
					<ElDropdownMenu>
						<ElDropdownItem
							v-for="list in allGuestLists"
							:key="list.id"
							:command="list.id"
						>
							{{ list.name }}
						</ElDropdownItem>
						<ElDropdownItem
							v-if="!!currentFilter"
							:command="null"
							class="border-t flex items-center gap-3"
							data-testid="remove-filter"
						>
							<Inline svg="ex" class="text-icon-subtle" />
							Remove Filter
						</ElDropdownItem>
					</ElDropdownMenu>
				</ElDropdown>

				<input
					v-if="searchOpen"
					ref="searchInput"
					v-model="currentSearch"
					type="text"
					autocomplete="off"
					placeholder="Search for a guest"
					class="guest-usage-table__search mr-2 ml-2"
				/>

				<CToolTip
					v-if="!searchOpen"
					placement="top"
					:use-button="false"
				>
					<template #button>
						<CIconButton
							class="mr-3"
							icon="search"
							aria-label="Open Guest Search"
							@click="searchClick"
						/>
					</template>
					<span>Search Guests</span>
				</CToolTip>

				<CIconButton
					v-else
					class="mr-3"
					icon="ex"
					aria-label="Close Guest Search"
					@click="searchClick"
				/>
			</div>
		</div>
		<input
			v-if="searchOpen"
			ref="searchInputMobile"
			v-model="currentSearch"
			type="text"
			autocomplete="off"
			placeholder="Search for a guest"
			class="guest-usage-table__search-full"
		/>

		<table
			class="table-style guest-usage-table__table w-full table-fixed"
			:class="{ 'guest-usage-table__table--has-actions': hasActions }"
		>
			<thead>
				<tr>
					<th class="sticky text-left flex items-center cursor-pointer select-none gap-1" @click="changeSort(0)">
						Email
						<template v-if="sortColumn === 0">
							<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
							<Inline v-else svg="sort-down" class="text-icon-subtle" />
						</template>
					</th>
					<th class="sticky text-left flex items-center cursor-pointer select-none gap-1">Type</th>
					<th class="sticky text-left b9:show flex items-center cursor-pointer select-none gap-1" @click="changeSort(1)">
						Emails
						<template v-if="sortColumn === 1">
							<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
							<Inline v-else svg="sort-down" class="text-icon-subtle" />
						</template>
					</th>
					<th class="sticky text-left b9:show flex items-center cursor-pointer select-none gap-1" @click="changeSort(2)">
						Reminders
						<template v-if="sortColumn === 2">
							<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
							<Inline v-else svg="sort-down" class="text-icon-subtle" />
						</template>
					</th>
					<th class="sticky text-left b9:show flex items-center cursor-pointer select-none gap-1" @click="changeSort(3)">
						Logins
						<template v-if="sortColumn === 3">
							<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
							<Inline v-else svg="sort-down" class="text-icon-subtle" />
						</template>
					</th>
					<th class="sticky text-left flex items-center cursor-pointer select-none gap-1" @click="changeSort(4)">
						Last Login
						<template v-if="sortColumn === 4">
							<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
							<Inline v-else svg="sort-down" class="text-icon-subtle" />
						</template>
					</th>
					<th v-if="hasActions" class="sticky text-left flex items-center cursor-pointer select-none gap-1"></th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="row in usageList" :key="row.id">
					<td class="truncate" data-tooltip>{{ row.email }}</td>
					<td>
						<template v-if="row.guestLists.length">
							<div
								v-for="list of row.guestLists"
								:key="list.form.id"
								:class="['py-1', { 'text-subtle line-through': !list.active }]"
							>
								<CToolTip
									placement="top"
									:disabled="!list.inactiveSince"
									:use-button="false"
									class="flex items-center cursor-default truncate"
								>
									<template #button>
										<div class="flex items-center gap-3 truncate">
											<Inline svg="person" class="text-icon-subtle" />
											<span class="truncate" data-tooltip>{{ list.form.name }}</span>
										</div>
									</template>
									<span>Disabled on <span class="italic">{{ list.form.name }}</span> on {{ list.inactiveSince }}</span>
								</CToolTip>
							</div>
						</template>
						<template v-else>
							<div class="truncate flex items-center gap-3 py-1">
								<Inline svg="link" class="text-icon-subtle" />
								Link
							</div>
						</template>
					</td>
					<td class="text-center b9:show">{{ row.emails }}</td>
					<td class="text-center b9:show">{{ row.reminders }}</td>
					<td class="text-center b9:show">{{ row.logins }}</td>
					<td class="text-center">{{ row.lastLogin }}</td>
					<td v-if="hasActions" class="text-center">
						<CToolTip
							v-for="action in row.actions"
							:key="action.name"
							:aria-label="action.name"
							placement="top"
							:use-button="false"
						>
							<template #button>
								<CIconButton
									:icon="action.icon"
									intense
									@click="handleAction(row.userId, action.name)"
								/>
							</template>
							<span>{{ action.name }}</span>
						</CToolTip>
					</td>
				</tr>
			</tbody>
		</table>
		<div v-if="usageList.length === 0 && currentSearch != ''" class="text-center py-4 border border-table">
			Your organization {{ selectedDate === date[0].value ? 'has' : 'had' }} no active guests matching <span class="italic">{{ currentSearch }}</span>
		</div>
		<div v-else-if="usageList.length === 0" class="text-center py-4 border border-table">
			Your organization {{ selectedDate === date[0].value ? 'has' : 'had' }} no active guests in {{ selectedDate }}
		</div>
	</div>
</template>
<script lang="ts">
	import { globalStore } from 'src/stores/global-store';
	import type { PropType } from 'vue';
	import { computed, defineComponent, nextTick, ref, onMounted, inject } from 'vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import { Dropdown, DropdownMenu, DropdownItem } from '@cognitoforms/element-ui';
	import CSubtleSelect from 'src/components/form-controls/SubtleSelect.vue';
	import longTextTooltip from 'src/mixins/long-text-tooltip';
	import type { GuestUsagePeriodViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-usage-period-view-model';
	import { viewAsUserKey } from 'src/utilities/injection-keys';
	import type { SubtleSelectOption } from 'src/components/form-controls/SubtleSelect.vue';

	type UsageRow = {
		id: string;
		userId: string;
		email: string;
		guestLists: GuestListStatus[];
		logins: number;
		emails: number;
		reminders: number;
		lastLogin: string;
		lastLoginDate: Date;
		actions: GuestAction[];
	}

	type GuestListStatus = {
		form: GuestList;
		active: boolean;
		inactiveSince: string;
	}

	type GuestList = {
		id: string;
		name: string;
	}

	type GuestAction = {
		name: string;
		icon: string;
	}

	export default defineComponent({
		components: {
			CBadge,
			CToolTip,
			CIconButton,
			CSubtleSelect,
			ElDropdown: Dropdown,
			ElDropdownMenu: DropdownMenu,
			ElDropdownItem: DropdownItem
		},
		mixins: [ longTextTooltip ],
		props: {
			guestUsage: {
				type: Array as PropType<GuestUsagePeriodViewModel[]>,
				required: true
			},
			guestAccessDisabled: {
				type: Date,
				default: null
			}
		},
		setup(props) {
			const currentFilter = ref(null);
			const currentFilterName = ref('');
			const currentSearch = ref('');
			const sortColumn = ref(0);
			const sortAscending = ref(true);
			const tableHeight = ref(0);
			const searchInput = ref<HTMLInputElement>(null);
			const searchInputMobile = ref<HTMLInputElement>(null);
			const guestUsageTable = ref<HTMLElement>(null);
			const viewAsUser = inject(viewAsUserKey);
			const canEnableViewAsUserMode = computed(() => !globalStore.isInViewAsUserMode);
			const hasActions = ref(canEnableViewAsUserMode.value);

			const setMinHeight = () => {
				if (guestUsageTable.value) {
					tableHeight.value = guestUsageTable.value.offsetHeight;
				}
			};

			const emailSort = (list: UsageRow[]) => {
				if (sortAscending.value)
					return list.sort((a, b) => a.email.localeCompare(b.email));
				return list.sort((a, b) => b.email.localeCompare(a.email));
			};

			const emailsSort = (list: UsageRow[]) => {
				if (sortAscending.value)
					return list.sort((a, b) => a.emails - b.emails);
				return list.sort((a, b) => b.emails - a.emails);
			};

			const remindersSort = (list: UsageRow[]) => {
				if (sortAscending.value)
					return list.sort((a, b) => a.reminders - b.reminders);
				return list.sort((a, b) => b.reminders - a.reminders);
			};

			const loginsSort = (list: UsageRow[]) => {
				if (sortAscending.value)
					return list.sort((a, b) => a.logins - b.logins);
				return list.sort((a, b) => b.logins - a.logins);
			};

			const lastLoginSort = (list: UsageRow[]) => {
				if (sortAscending.value)
					return list.sort((a, b) => a.lastLoginDate.getTime() - b.lastLoginDate.getTime());
				return list.sort((b, a) => a.lastLoginDate.getTime() - b.lastLoginDate.getTime());
			};

			const sorts = [emailSort, emailsSort, remindersSort, loginsSort, lastLoginSort];

			const applySort = (list: UsageRow[]) => {
				const sortFunction = sorts[sortColumn.value];
				return sortFunction(list);
			};

			const changeSort = (column: number) => {
				if (sortColumn.value === column) {
					sortAscending.value = !sortAscending.value;
				}
				else {
					sortColumn.value = column;
					sortAscending.value = true;
				}
			};

			const date = computed<SubtleSelectOption[]>(() =>
				props.guestUsage.map(g => ({
					label: g.DisplayName,
					value: g.DisplayName
				}))
			);

			const selectedDate = ref(props.guestUsage[0].DisplayName);

			const usageList = computed(() => {
				const list: UsageRow[] = [];
				const usageForSelectedPeriod = props.guestUsage.find(g => g.DisplayName === selectedDate.value)?.UsageDetails ?? [];

				for (const usage of usageForSelectedPeriod) {
					const email = usage.Email || 'Unknown';

					if (!!currentSearch.value && !email.includes(currentSearch.value))
						continue;

					const guestListIds = new Map<string, boolean>();
					const guestListInactiveSince = new Map<string, Date>();
					for (const reference of usage.UsageData.GuestReferences) {
						const formId = reference.EntryId.substring(0, reference.EntryId.indexOf('-'));

						if (reference.IsGuest && reference.InactiveSince == null && globalStore.organization.Plan.AllowGuests)
							guestListIds.set(formId, true);
						else if (!guestListIds.get(formId)) {
							guestListIds.set(formId, false);
							// If there are multiple inactive entries for the same form, we want to show the most recent one
							const existingInactiveSince = guestListInactiveSince.get(formId) ?? new Date(0);
							if (new Date(reference.InactiveSince) > existingInactiveSince)
								guestListInactiveSince.set(formId, new Date(reference.InactiveSince));
							else if (!globalStore.organization.Plan.AllowGuests && new Date(props.guestAccessDisabled) > existingInactiveSince)
								guestListInactiveSince.set(formId, new Date(props.guestAccessDisabled));
						}
					}

					if (!!currentFilter.value && !Array.from(guestListIds.keys()).includes(currentFilter.value))
						continue;

					const guestStatusList: GuestListStatus[] = [];
					for (const [formId, active] of guestListIds) {
						const form = globalStore.allForms[formId] ? { id: globalStore.allForms[formId].Id, name: globalStore.allForms[formId].Name } : { id: formId, name: 'Deleted' } as GuestList;
						const inactiveSince = active ? null : guestListInactiveSince.get(formId)?.toLocaleDateString('en-us');

						guestStatusList.push({ form, active, inactiveSince });
					}

					let displayedLoginDate: string;
					if (usage.LastLogin)
						displayedLoginDate = new Date(usage.LastLogin).toLocaleDateString('en-us');
					else
						displayedLoginDate = '-';

					const actions: GuestAction[] = [];

					if (canEnableViewAsUserMode.value) {
						actions.push(
							{
								name: 'View As Guest',
								icon: 'login'
							}
						);
					}

					list.push({
						id: usage.UsageData.Id,
						userId: usage.UsageData.UserId,
						email: email,
						guestLists: guestStatusList,
						logins: usage.UsageData.Logins,
						emails: usage.UsageData.Emails,
						reminders: usage.UsageData.Reminders,
						lastLoginDate: new Date(usage.LastLogin) ?? new Date(0),
						lastLogin: displayedLoginDate,
						actions: actions
					});
				}
				return applySort(list);
			});

			const buildAllGuestLists = () => {
				const uniqueGuestLists = [];
				const uniqueIds = new Set();

				for (const usage of usageList.value) {
					for (const guestStatus of usage.guestLists) {
						const guestId = guestStatus.form.id;

						if (!uniqueIds.has(guestId) && globalStore.allForms[guestId]) {
							uniqueIds.add(guestId);
							uniqueGuestLists.push({
								id: guestId,
								name: guestStatus.form.name
							});
						}
					}
				}

				return uniqueGuestLists.sort((a, b) => a.name.localeCompare(b.name));
			};

			const allGuestLists = ref<GuestList[]>(buildAllGuestLists());

			const searchOpen = ref(false);

			const searchClick = () => {
				currentSearch.value = '';
				searchOpen.value = !searchOpen.value;

				if (searchOpen.value) {
					nextTick(() => {
						searchInput.value?.focus();
						searchInputMobile.value?.focus();
					});
				}
			};

			const handleFilterChange = (command: string) => {
				currentFilter.value = command;

				if (globalStore.allForms[command]) {
					currentFilterName.value = globalStore.allForms[command].Name;
				}
				else
					currentFilterName.value = null;
			};

			const updateDate = (value: string) => {
				currentSearch.value = '';
				currentFilter.value = null;
				allGuestLists.value = buildAllGuestLists();
				searchOpen.value = false;
				selectedDate.value = value;
				setMinHeight();
			};

			const handleAction = async (id, name) => {
				if (name === 'View As Guest')
					await viewAsUser.handleEnterViewAsUserMode(id, '/settings/guest-access');
			};

			onMounted(() => {
				setMinHeight();
			});

			return {
				usageList,
				searchOpen,
				searchClick,
				currentFilter,
				handleFilterChange,
				handleAction,
				date,
				selectedDate,
				updateDate,
				allGuestLists,
				currentFilterName,
				currentSearch,
				changeSort,
				sortColumn,
				sortAscending,
				searchInput,
				searchInputMobile,
				guestUsageTable,
				tableHeight,
				hasActions
			};
		}
	});
</script>
<style lang="scss">
.guest-usage-table {
	min-height: var(--table-height);

	&__header {
		top: 54px;

		@include b-up(700px) {
			top: 0;
		}
	}

	&__table {
		display: grid;
		border: 1px solid var(--border-table);
		grid-template-columns: minmax(70px, 2fr) minmax(90px, 1fr) auto auto auto auto;

		@include b-down($b9) {
			grid-template-columns: minmax(70px, 2fr) minmax(90px, 1fr) auto;
		}

		&--has-actions {
			grid-template-columns: minmax(70px, 2fr) minmax(90px, 1fr) auto auto auto auto auto;

			@include b-down($b9) {
				grid-template-columns: minmax(70px, 2fr) minmax(90px, 1fr) auto auto;
			}
		}

		tr:not(:last-child) td {
			border-bottom: 1px solid var(--border-table);
		}

		tr,
		thead,
		tbody {
			display: contents;
		}

		td {
			align-content: center;
		}

		thead th {
			top: 103px;

			@include b-up(700px) {
				top: 41px;
			}
		}
	}

	&__badge {
		max-width: 200px;
	}

	@include b-up(1100px) {

		&__search {
			display: block;

			&-full {
				display: none;
			}
		}
	}

	@include b-up(1100px) {

		&__search {
			display: block;

			&-full {
				display: none;
			}
		}
	}

	@include b-down(1100px) {

		&__search {
			display: none;

			&-full {
				display: block;
			}
		}
	}
}
</style>
